import { ApolloError, gql } from "@apollo/client";
import { isContentTooLargeError } from "../utils/prepareAiRenderSnapshot";

export const FLOORPLAN_AI_RENDER_CREDIT_COST = 2;
export const FLOORPLAN_AI_RENDER_MODEL = "google/gemini-3.1-flash-image";
/** AI credits charged for animating a render into a short video. */
export const FLOORPLAN_AI_VIDEO_CREDIT_COST = 5;

export const RenderFloorplanSceneWithAIDocument = gql`
  mutation RenderFloorplanSceneWithAI(
    $manifest: JSONObject!
    $snapshot: Upload!
    $stylePreset: String
    $prompt: String
    $model: String
    $name: String
  ) {
    renderFloorplanSceneWithAI(
      input: {
        manifest: $manifest
        snapshot: $snapshot
        stylePreset: $stylePreset
        prompt: $prompt
        model: $model
        name: $name
      }
    ) {
      imageUrl
      model
      manifestDigest
      createdAt
      userItemId
    }
  }
`;

export const LogFloorplanAiRenderErrorDocument = gql`
  mutation LogFloorplanAiRenderError($input: LogFloorplanAiRenderErrorInput!) {
    logFloorplanAiRenderError(input: $input)
  }
`;

/**
 * Queue a floorplan AI-render image as a short MP4.
 * Poll {@link AiVideoJobDocument} until COMPLETED. Charges {@link FLOORPLAN_AI_VIDEO_CREDIT_COST} on success.
 */
export const GenerateAiVideoDocument = gql`
  mutation GenerateAiVideo($moodBoardId: ID, $imageUrl: String, $prompt: String) {
    generateAiVideo(moodBoardId: $moodBoardId, imageUrl: $imageUrl, prompt: $prompt) {
      jobId
      status
      videoUrl
      creditsConsumed
      errorMessage
      userItem {
        id
        name
        images
        videos
        tags
      }
    }
  }
`;

export const AiVideoJobDocument = gql`
  query AiVideoJob($id: ID!) {
    aiVideoJob(id: $id) {
      jobId
      status
      videoUrl
      creditsConsumed
      errorMessage
      userItem {
        id
        name
        images
        videos
        tags
      }
    }
  }
`;

export type AiVideoJobResult = {
  jobId: string;
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  videoUrl?: string | null;
  creditsConsumed: number;
  errorMessage?: string | null;
};

const AI_VIDEO_POLL_INTERVAL_MS = 4_000;
const AI_VIDEO_POLL_MAX_WAIT_MS = 12 * 60 * 1000;

/**
 * Poll until the video job completes or fails. First fetch runs immediately.
 */
export async function waitForAiVideoJob(
  fetchJob: () => Promise<AiVideoJobResult | null | undefined>,
): Promise<AiVideoJobResult> {
  const startedAt = Date.now();
  while (true) {
    const job = await fetchJob();
    if (job?.status === "COMPLETED" && job.videoUrl) {
      return job;
    }
    if (job?.status === "FAILED") {
      throw new Error(job.errorMessage || "Video generation failed");
    }
    if (Date.now() - startedAt >= AI_VIDEO_POLL_MAX_WAIT_MS) {
      throw new Error(
        "Video generation timed out. Check Uploads → Videos in a few minutes.",
      );
    }
    await new Promise((resolve) => setTimeout(resolve, AI_VIDEO_POLL_INTERVAL_MS));
  }
}

/**
 * Server-side GraphQL failures are already written to AIRenderLog by
 * renderFloorplanSceneWithAI. Report only client/network failures (413, prep).
 */
export function shouldReportFloorplanAiRenderError(error: unknown): boolean {
  if (isContentTooLargeError(error)) return true;
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    return false;
  }
  return true;
}

export function getFloorplanAiRenderErrorLogMessage(error: unknown): string {
  if (isContentTooLargeError(error)) {
    return "HTTP 413 Content Too Large";
  }
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error ?? "Unknown error");
}
